/**
 * ChannelPackageCard Component V2
 *
 * Premium Status-Karte für Channel Packages.
 * Zeigt Status, Bilder und Actions mit einem eleganten Design.
 *
 * Built with Tailwind CSS v4.1
 * WCAG 2.1 Level AA + BFSG konform
 */

import {
  Eye,
  Pencil,
  History,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Clock,
  ShoppingBag,
  Globe,
  Store,
  Smartphone,
  Mail,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { createElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChannelPackage } from '@/types/channel-packages';
import {
  useChannelPackageStatus,
  formatLastRegenerated,
} from '@/hooks/useChannelPackage';
import { RegenerateButton } from './RegenerateButton';
import { getAssetThumbnailUrl } from '@/lib/helpers/asset-urls';
import { ReadinessScoreBar } from '@/components/v2/channel-readiness/ReadinessScoreBar';
import { Button, Badge, Card, CardContent, CardFooter } from '@/components/v2/ui';
import { cn } from '@/lib/utils';

// Channel type icon mapping
const CHANNEL_ICONS: Record<string, LucideIcon> = {
  amazon: ShoppingBag,
  ebay: Store,
  shopify: Store,
  woocommerce: Globe,
  magento: Globe,
  website: Globe,
  app: Smartphone,
  email: Mail,
  pdf: FileText,
  default: Globe,
};

function getChannelIcon(channelType: unknown): LucideIcon {
  if (typeof channelType !== 'string') return CHANNEL_ICONS.default;
  return CHANNEL_ICONS[channelType.toLowerCase()] || CHANNEL_ICONS.default;
}

// Status configuration
interface StatusConfig {
  labelKey: string;
  variant: 'success' | 'warning' | 'error' | 'secondary' | 'info';
  icon: LucideIcon;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

function getStatusConfig(status: string): StatusConfig {
  const configs: Record<string, StatusConfig> = {
    current: {
      labelKey: 'channelPackages.statusCurrent',
      variant: 'success',
      icon: CheckCircle,
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
      borderColor: 'border-emerald-200 dark:border-emerald-800',
      textColor: 'text-emerald-700 dark:text-emerald-400',
    },
    outdated: {
      labelKey: 'channelPackages.statusOutdated',
      variant: 'warning',
      icon: AlertTriangle,
      bgColor: 'bg-amber-50 dark:bg-amber-900/30',
      borderColor: 'border-amber-200 dark:border-amber-800',
      textColor: 'text-amber-700 dark:text-amber-400',
    },
    error: {
      labelKey: 'channelPackages.statusError',
      variant: 'error',
      icon: XCircle,
      bgColor: 'bg-red-50 dark:bg-red-900/30',
      borderColor: 'border-red-200 dark:border-red-800',
      textColor: 'text-red-700 dark:text-red-400',
    },
    processing: {
      labelKey: 'channelPackages.statusProcessing',
      variant: 'info',
      icon: RefreshCw,
      bgColor: 'bg-primary-50 dark:bg-primary-900/30',
      borderColor: 'border-primary-200 dark:border-primary-800',
      textColor: 'text-primary-700 dark:text-primary-400',
    },
    pending: {
      labelKey: 'channelPackages.statusPending',
      variant: 'secondary',
      icon: Clock,
      bgColor: 'bg-neutral-50 dark:bg-neutral-800',
      borderColor: 'border-neutral-200 dark:border-neutral-700',
      textColor: 'text-neutral-600 dark:text-neutral-400',
    },
  };

  return configs[status] || configs.pending;
}

interface ReadinessData {
  readiness_score: number;
  image_rule_score: number;
  text_rule_score: number;
  issue_count: number;
}

interface ChannelPackageCardProps {
  package_: ChannelPackage;
  contentUpdatedAt?: string;
  onPreview?: () => void;
  onEdit?: () => void;
  onRegenerate?: () => void;
  onViewVersions?: () => void;
  isRegenerating?: boolean;
  readiness?: ReadinessData | null;
}

export function ChannelPackageCard({
  package_: pkg,
  contentUpdatedAt,
  onPreview,
  onEdit,
  onRegenerate,
  onViewVersions,
  isRegenerating = false,
  readiness,
}: ChannelPackageCardProps) {
  const { t } = useTranslation();
  const statusInfo = useChannelPackageStatus(pkg, contentUpdatedAt);
  const statusConfig = getStatusConfig(statusInfo.status);
  const channelIcon = getChannelIcon(pkg.channel?.channel_type || '');
  const StatusIcon = statusConfig.icon;

  const hasImages =
    pkg.current_version?.asset_renditions &&
    pkg.current_version.asset_renditions.length > 0;

  return (
    <Card
      className={cn(
        'group relative h-full overflow-hidden transition-all duration-300',
        'hover:shadow-lg hover:-translate-y-0.5',
        'border-2',
        statusConfig.borderColor
      )}
    >
      {/* Status indicator bar at top */}
      <div
        className={cn(
          'absolute top-0 left-0 right-0 h-1',
          statusInfo.status === 'current' && 'bg-emerald-500',
          statusInfo.status === 'outdated' && 'bg-amber-500',
          statusInfo.status === 'error' && 'bg-red-500',
          statusInfo.status === 'processing' && 'bg-primary-500 animate-pulse',
          statusInfo.status === 'pending' && 'bg-neutral-300 dark:bg-neutral-600'
        )}
      />

      <CardContent className="pt-5 pb-4">
        {/* Header: Channel Info + Status */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            {/* Channel Icon */}
            <div
              className={cn(
                'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
                'bg-gradient-to-br from-neutral-100 to-neutral-50 dark:from-neutral-800 dark:to-neutral-900',
                'border border-neutral-200 dark:border-neutral-700',
                'group-hover:from-neutral-50 group-hover:to-white dark:group-hover:from-neutral-700 dark:group-hover:to-neutral-800',
                'transition-all duration-300'
              )}
            >
              {createElement(channelIcon, { className: "h-5 w-5 text-neutral-600 dark:text-neutral-400" })}
            </div>

            {/* Channel Name + Type */}
            <div className="min-w-0">
              <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 truncate leading-tight">
                {pkg.channel?.name || t('channelPackages.unknownChannel')}
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                {pkg.channel?.channel_type || t('channelPackages.unknown')}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <Badge
            variant={statusConfig.variant}
            className="flex-shrink-0 gap-1"
          >
            <StatusIcon
              className={cn(
                'h-3.5 w-3.5',
                statusInfo.status === 'processing' && 'animate-spin'
              )}
            />
            {t(statusConfig.labelKey)}
          </Badge>
        </div>

        {/* Error Message */}
        {statusInfo.status === 'error' && pkg.last_regenerate_error && (
          <div
            className={cn(
              'mb-4 p-3 rounded-lg',
              'bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800',
              'text-sm text-red-700 dark:text-red-400'
            )}
          >
            <div className="flex items-start gap-2">
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p className="line-clamp-2">{pkg.last_regenerate_error}</p>
            </div>
          </div>
        )}

        {/* Processing State */}
        {statusInfo.status === 'processing' && (
          <div
            className={cn(
              'mb-4 p-3 rounded-lg',
              'bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800',
              'text-sm text-primary-700 dark:text-primary-400'
            )}
          >
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>{t('channelPackages.regeneratingStatus')}</span>
            </div>
          </div>
        )}

        {/* Readiness Score */}
        {readiness && (
          <div className="mb-4 p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                Readiness
              </span>
              {readiness.issue_count > 0 && (
                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                  {readiness.issue_count} Issue{readiness.issue_count !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <ReadinessScoreBar score={readiness.readiness_score} size="sm" />
          </div>
        )}

        {/* Meta Info */}
        <div className="space-y-2 mb-4 text-sm">
          <div className="flex justify-between items-center py-1.5 border-b border-neutral-100 dark:border-neutral-800">
            <span className="text-neutral-500 dark:text-neutral-400">{t('channelPackages.lastRegeneration')}</span>
            <span className="font-medium text-neutral-700 dark:text-neutral-300">
              {formatLastRegenerated(pkg.last_regenerated_at)}
            </span>
          </div>
          {pkg.current_version && (
            <div className="flex justify-between items-center py-1.5">
              <span className="text-neutral-500 dark:text-neutral-400">{t('channelPackages.currentVersion')}</span>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
                  'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'
                )}
              >
                V{pkg.current_version.version_number}
              </span>
            </div>
          )}
        </div>

        {/* Image Gallery */}
        {hasImages && (
          <div className="mb-2">
            <div className="flex gap-2 flex-wrap">
              {pkg.current_version!.asset_renditions!.slice(0, 4).map(
                (rendition, idx) => (
                  <div
                    key={rendition.asset_id || idx}
                    className={cn(
                      'relative w-16 h-16 rounded-lg overflow-hidden',
                      'border border-neutral-200 dark:border-neutral-700',
                      'bg-neutral-50 dark:bg-neutral-800',
                      'group/img transition-transform duration-200',
                      'hover:scale-105 hover:shadow-md hover:z-10'
                    )}
                  >
                    <img
                      src={getAssetThumbnailUrl(rendition)}
                      alt={rendition.alt_text || t('channelPackages.imageSlotAlt', { slot: rendition.slot })}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Slot Badge */}
                    <span
                      className={cn(
                        'absolute top-1 left-1',
                        'px-1.5 py-0.5 rounded text-2xs font-bold',
                        'bg-black/60 text-white backdrop-blur-sm'
                      )}
                    >
                      #{rendition.slot}
                    </span>
                  </div>
                )
              )}
              {pkg.current_version!.asset_renditions!.length > 4 && (
                <div
                  className={cn(
                    'w-16 h-16 rounded-lg',
                    'border border-neutral-200 dark:border-neutral-700 border-dashed',
                    'bg-neutral-50 dark:bg-neutral-800',
                    'flex items-center justify-center',
                    'text-sm font-medium text-neutral-500 dark:text-neutral-400'
                  )}
                >
                  +{pkg.current_version!.asset_renditions!.length - 4}
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              {t('channelPackages.imagesCount_other', { count: pkg.current_version!.asset_renditions!.length })}
            </p>
          </div>
        )}
      </CardContent>

      {/* Actions Footer */}
      <CardFooter
        className={cn(
          'pt-0 pb-4 border-t border-neutral-100 dark:border-neutral-800',
          'flex items-center justify-between gap-2'
        )}
      >
        <div className="flex gap-1">
          {onPreview && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onPreview}
              aria-label={t('channelPackages.previewAriaLabel')}
              className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label={t('channelPackages.editAriaLabel')}
              className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {onViewVersions && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewVersions}
              aria-label={t('channelPackages.versionsAriaLabel')}
              className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <History className="h-4 w-4" />
            </Button>
          )}
        </div>

        {onRegenerate && (
          <RegenerateButton
            onClick={onRegenerate}
            isRegenerating={isRegenerating}
            size="sm"
            showLabel={false}
          />
        )}
      </CardFooter>
    </Card>
  );
}

/**
 * Compact Row for list/table views
 */
interface ChannelPackageRowProps {
  package_: ChannelPackage;
  contentUpdatedAt?: string;
  onPreview?: () => void;
  onEdit?: () => void;
  onRegenerate?: () => void;
  onViewVersions?: () => void;
  isRegenerating?: boolean;
}

export function ChannelPackageRow({
  package_: pkg,
  contentUpdatedAt,
  onPreview,
  onEdit,
  onRegenerate,
  onViewVersions,
  isRegenerating = false,
}: ChannelPackageRowProps) {
  const { t } = useTranslation();
  const statusInfo = useChannelPackageStatus(pkg, contentUpdatedAt);
  const statusConfig = getStatusConfig(statusInfo.status);
  const channelIcon = getChannelIcon(pkg.channel?.channel_type || '');
  const StatusIcon = statusConfig.icon;

  return (
    <tr
      className={cn(
        'group border-b border-neutral-100 dark:border-neutral-800 last:border-0',
        'hover:bg-neutral-50/50 dark:hover:bg-neutral-800/50 transition-colors duration-150'
      )}
    >
      {/* Channel */}
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center',
              'bg-neutral-100 dark:bg-neutral-800 group-hover:bg-white dark:group-hover:bg-neutral-700',
              'border border-neutral-200 dark:border-neutral-700',
              'transition-colors duration-150'
            )}
          >
            {createElement(channelIcon, { className: "h-4 w-4 text-neutral-600 dark:text-neutral-400" })}
          </div>
          <span className="font-medium text-neutral-900 dark:text-neutral-50">
            {pkg.channel?.name || t('channelPackages.unknown')}
          </span>
        </div>
      </td>

      {/* Type */}
      <td className="py-4 px-4">
        <span className="text-neutral-500 dark:text-neutral-400">
          {pkg.channel?.channel_type || t('channelPackages.unknown')}
        </span>
      </td>

      {/* Status */}
      <td className="py-4 px-4">
        <Badge variant={statusConfig.variant} className="gap-1">
          <StatusIcon
            className={cn(
              'h-3.5 w-3.5',
              statusInfo.status === 'processing' && 'animate-spin'
            )}
          />
          {t(statusConfig.labelKey)}
        </Badge>
      </td>

      {/* Last Regeneration */}
      <td className="py-4 px-4">
        <span className="text-neutral-500 dark:text-neutral-400 text-sm">
          {formatLastRegenerated(pkg.last_regenerated_at)}
        </span>
      </td>

      {/* Actions */}
      <td className="py-4 px-4">
        <div className="flex items-center justify-end gap-1">
          {onPreview && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onPreview}
              aria-label={t('channelPackages.previewAriaLabelRow')}
              className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label={t('channelPackages.editAriaLabel')}
              className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {onViewVersions && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewVersions}
              aria-label={t('channelPackages.versionsAriaLabelRow')}
              className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <History className="h-4 w-4" />
            </Button>
          )}
          {onRegenerate && (
            <RegenerateButton
              onClick={onRegenerate}
              isRegenerating={isRegenerating}
              size="sm"
              showLabel={false}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

